import { computed, inject, Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
} from '@qtek/core/websockets-core';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import { DocumentTemplate, TemplateTypes } from '@qtek/shared/models';
import { isNil } from '@qtek/shared/utils';
import { filter, mergeMap, pipe, Subject, takeUntil, tap } from 'rxjs';
import { TemplateDataStore } from '../template-data/template-data.store';
import { TemplatesService } from './templates.service';
import {
  applySelectableTemplateDeleteSuccess,
  applySelectableTemplateInsertSuccess,
  applySelectableTemplatesQuery,
  applySelectableTemplateUpdateSuccess,
  applyTemplateDeleteSuccess,
  applyTemplateInsertSuccess,
  applyTemplatesQuery,
  applyTemplateUpdateSuccess,
  templatesInitialState,
  withTemplateType,
} from './templates.updaters';

export { TemplatesState } from './templates.updaters';

interface TemplatesOpMessage extends BaseWebsocketMessage {
  id?: string;
  res?: DocumentTemplate | DocumentTemplate[];
  sts?: Record<string, any>;
}

export const TemplatesStore = signalStore(
  { providedIn: 'root' },
  withState(templatesInitialState),
  withProps(() => ({
    templatesOps$: new Subject<{
      op: string;
      ok: boolean;
      res: DocumentTemplate | DocumentTemplate[] | undefined;
      sts: Record<string, any> | undefined;
    }>(),
  })),
  withMethods(
    (
      store,
      templatesService = inject(TemplatesService),
      templateDataStore = inject(TemplateDataStore),
      snackBar = inject(SnackBarService)
    ) => {
      const disconnect$ = new Subject<void>();
      const disconnectSelectable$ = new Subject<void>();
      // Every mounted qt-template-selector calls connectSelectable/disconnectSelectable
      // independently — ref-count so N instances share one underlying WS subscription
      // instead of each opening (and closing) their own.
      let selectableSubscriberCount = 0;
      let activeSelectablePayload: Partial<
        Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
      > | null = null;
      const templateByIdCache = new Map<
        string,
        Signal<DocumentTemplate | undefined>
      >();

      // `prms.auditNames=true` the WS subscribe query must carry so `_cby`/`_uby`
      // come back with the actor's display name (see `base-api.model.ts` AuditUser)
      // — same as `template-data.store.ts`. Without it the Templates list renders
      // no names, and freshly created rows show no dates at all.
      const templatesSubscribePrms = { auditNames: 'true' };

      const templatesConnectMysid = 'templates-details';
      const templatesConnectSelectableMysid = 'templates-selectable';

      const handleTemplatesOp = ({
        op,
        id,
        res,
        sts,
      }: TemplatesOpMessage): void => {
        const ok = !!res || !isNil(sts?.['meta']);
        // Consumers read `tp`; the raw ack only carries the API's `type`.
        const normalizedRes = Array.isArray(res)
          ? res.map(withTemplateType)
          : res && withTemplateType(res);
        store.templatesOps$.next({ op, ok, res: normalizedRes, sts });

        switch (op) {
          case 'query':
            patchState(
              store,
              res || !isNil(sts?.['meta'])
                ? applyTemplatesQuery(res as DocumentTemplate[], sts?.['meta'])
                : { templatesIsLoading: false }
            );
            break;
          case 'ins':
            if (res || !isNil(sts?.['meta'])) {
              patchState(store, state =>
                applyTemplateInsertSuccess(state, res as DocumentTemplate)
              );
              snackBar.showSnackBar({
                type: 'NormalMessage',
                message: 'GNR_TEMPLATE_CREATED',
              } as NormalSnackBarData);
            } else {
              // matches original effect, which mapped this failure to a
              // query-failure action — no create-failure snackbar ever fired
              patchState(store, { templatesIsLoading: false });
            }
            break;
          case 'upd':
            if (res || !isNil(sts?.['meta'])) {
              patchState(store, state =>
                applyTemplateUpdateSuccess(state, res as DocumentTemplate)
              );
              if (Date.now() - templateDataStore.lastPublishTime() > 2000) {
                snackBar.showSnackBar({
                  type: 'NormalMessage',
                  message: 'GNR_TEMPLATE_UPDATED',
                } as NormalSnackBarData);
              }
            } else {
              patchState(store, { templatesIsLoading: false });
              snackBar.showSnackBar({
                type: 'ErrorMessage',
                message: 'GNR_TEMPLATE_UPDATE_FAILED',
              } as ErrorSnackBarData);
            }
            break;
          case 'del':
            if (res || !isNil(sts?.['meta'])) {
              patchState(store, state =>
                applyTemplateDeleteSuccess(state, id as string)
              );
              snackBar.showSnackBar({
                type: 'NormalMessage',
                message: 'GNR_TEMPLATE_DELETED',
              } as NormalSnackBarData);
            } else {
              patchState(store, { templatesIsLoading: false });
              snackBar.showSnackBar({
                type: 'ErrorMessage',
                message: 'GNR_TEMPLATE_DELETE_FAILED',
              } as ErrorSnackBarData);
            }
            break;
        }
      };

      return {
        connect: rxMethod<
          Partial<Omit<SubWebsocketMessageRequest, 'ent' | 'op'>>
        >(
          pipe(
            tap(() => patchState(store, { templatesIsLoading: true })),
            mergeMap(payload =>
              templatesService
                .subscribe({
                  ...payload,
                  mysid: templatesConnectMysid,
                  prms: {
                    ...templatesSubscribePrms,
                    ...(payload.prms ?? {}),
                  },
                })
                .pipe(takeUntil(disconnect$), tap(handleTemplatesOp))
            )
          )
        ),

        connectSelectable: rxMethod<
          Partial<Omit<SubWebsocketMessageRequest, 'ent' | 'op'>>
        >(
          pipe(
            tap(payload => {
              selectableSubscriberCount++;
              if (selectableSubscriberCount === 1) {
                activeSelectablePayload = payload;
              } else if (
                JSON.stringify(payload) !==
                JSON.stringify(activeSelectablePayload)
              ) {
                console.warn(
                  'TemplatesStore.connectSelectable: the selectable WS channel is shared by ' +
                    'subscriber count — this payload differs from the active one and will be ignored.',
                  payload
                );
              }
            }),
            filter(() => selectableSubscriberCount === 1),
            mergeMap(payload =>
              templatesService
                .subscribe({
                  ...payload,
                  view: 'selectable',
                  mysid: templatesConnectSelectableMysid,
                  prms: {
                    ...templatesSubscribePrms,
                    ...(payload.prms ?? {}),
                  },
                })
                .pipe(
                  takeUntil(disconnectSelectable$),
                  tap(({ op, id, res }: TemplatesOpMessage) => {
                    switch (op) {
                      case 'query':
                        patchState(
                          store,
                          applySelectableTemplatesQuery(
                            res as DocumentTemplate[] | undefined
                          )
                        );
                        break;
                      case 'ins':
                        if (res) {
                          patchState(store, state =>
                            applySelectableTemplateInsertSuccess(
                              state,
                              res as DocumentTemplate
                            )
                          );
                        }
                        break;
                      case 'upd':
                        if (res) {
                          patchState(store, state =>
                            applySelectableTemplateUpdateSuccess(
                              state,
                              res as DocumentTemplate
                            )
                          );
                        }
                        break;
                      case 'del':
                        patchState(store, state =>
                          applySelectableTemplateDeleteSuccess(
                            state,
                            id as string
                          )
                        );
                        break;
                    }
                  })
                )
            )
          )
        ),

        disconnectSelectable(): void {
          selectableSubscriberCount = Math.max(
            0,
            selectableSubscriberCount - 1
          );
          if (selectableSubscriberCount === 0) {
            activeSelectablePayload = null;
            disconnectSelectable$.next();
          }
        },

        selectableTemplatesByType(
          type: DocumentTemplate['tp']
        ): DocumentTemplate[] {
          return store.selectableTemplates().filter(t => t.tp === type);
        },

        disconnect(): void {
          disconnect$.next();
        },

        createTemplate(
          templateName: string,
          templateType: TemplateTypes
        ): void {
          templatesService.createTemplate(templateName, templateType);
        },

        updateTemplate(templateName: string, templateId: string): void {
          templatesService.updateTemplate(templateName, templateId);
        },

        deleteTemplate(templateId: string): void {
          templatesService.deleteTemplate(templateId);
        },

        templateById(id: string): Signal<DocumentTemplate | undefined> {
          let cached = templateByIdCache.get(id);
          if (!cached) {
            cached = computed(() =>
              store.templates().find(template => template._id === id)
            );
            templateByIdCache.set(id, cached);
          }
          return cached;
        },

        templatesByType(type: DocumentTemplate['tp']): DocumentTemplate[] {
          return store.templates().filter(template => template.tp === type);
        },
      };
    }
  )
);
